/**
 * Role a {@link Contact} plays at a {@link ContactCompany}. Today the CRM only
 * distinguishes the principal (owner) from delegated managers; extend with
 * additional values as new use cases emerge.
 */
export type ContactCompanyRole = "owner" | "manager" | "partner";

/**
 * Reference to a {@link Contact} associated with a company, including the
 * contact's role at the company. Contact identity stays the source of truth on
 * the `contacts` collection; this struct only stores the link metadata.
 */
export interface ContactCompanyMember {
  /** Document id of the linked contact in the `contacts` collection. */
  contactId: string;
  /** Role this contact holds at the company. */
  role: ContactCompanyRole;
  /** When the contact was first added to this company. */
  addedAt: Date;
}

import type { CrmActivityDomain } from "./contacts";

export type { CrmActivityDomain } from "./contacts";

/**
 * Activity kinds logged against a {@link ContactCompany}. Mirrors
 * {@link ContactActivityType} so timelines can be merged across contacts +
 * companies without translation.
 */
export type ContactCompanyActivityType =
  | "call"
  | "sms"
  | "email"
  | "note"
  | "task"
  | "lead"
  | "shipping_repairs";

/** Lightweight history entry stored under {@link ContactCompany.activities}. */
export interface ContactCompanyActivity {
  type: ContactCompanyActivityType;
  /** Free-text body. For `task`, this is the task's title for quick display. */
  note: string;
  /** Linked task doc id when {@link type} is `task`. */
  taskId?: string;
  /** Linked lead doc id when {@link type} is `lead`. */
  leadId?: string;
  /** Linked contact doc id when activity was produced by a contact. */
  contactId?: string;
  /**
   * Sales vs inventory partition. Omit on legacy docs (treated as `"sales"`).
   */
  domain?: CrmActivityDomain;
  createdAt: Date;
  createdByUserId: string;
}

/**
 * Matches {@link salesReps} entry `id` values (number or string).
 * `null` means the company has not yet been claimed by a rep.
 */
export type ContactCompanyOwnerId = string | number | null;

/**
 * CRM record for a company / organization. Mirrors {@link Contact} for
 * consistency: same shape for ownership, activities, dropoff handling, etc.
 *
 * Stored in the top-level Firestore collection `contact_companies`.
 */
export interface ContactCompany {
  id: string;
  /** Display name, e.g. "Acme Trucking, LLC". */
  name: string;
  /** Public website / marketing URL (optional). */
  website?: string;
  /** Free-form notes for sales context (optional). */
  notes?: string;
  /** Sales rep who owns this company. `null` = unclaimed. */
  ownerId: ContactCompanyOwnerId;
  /** `pickup_locations` doc id for the company's business address. */
  businessAddressId?: string | null;
  /**
   * `pickup_locations` doc ids for deliver-to / dropoff addresses.
   * The first entry is the default when a lead pre-fills from this company.
   */
  dropoffLocationIds: string[];
  /**
   * Associated {@link Terminal} document ids. Many-to-many is stored only on
   * the company side; contacts inherit terminals transitively through their
   * company memberships.
   */
  terminalIds: string[];
  /** Associated contacts + their roles. */
  members: ContactCompanyMember[];
  /**
   * Timestamp of the most recent activity (note added, lead update, contact
   * change, etc.) — used to sort the CRM list. `null` when no activity is
   * recorded yet.
   */
  lastActivityAt: Date | null;
  /**
   * Recent company-level activity log (calls, SMS, emails, notes, tasks,
   * lead-related events from associated contacts).
   */
  activities: ContactCompanyActivity[];
  createdAt: Date;
  updatedAt: Date;
  createdByUserId: string;
}

/** Normalize stored dropoff ids, including legacy single-id docs. */
export function normalizeContactCompanyDropoffLocationIds(
  ids: unknown,
  legacySingleId?: unknown,
): string[] {
  const out: string[] = [];
  if (Array.isArray(ids)) {
    for (const entry of ids) {
      if (typeof entry !== "string") continue;
      const trimmed = entry.trim();
      if (!trimmed || out.includes(trimmed)) continue;
      out.push(trimmed);
    }
  }
  if (out.length === 0) {
    const legacy =
      typeof legacySingleId === "string" && legacySingleId.trim()
        ? legacySingleId.trim()
        : undefined;
    if (legacy) out.push(legacy);
  }
  return out;
}

/** Default dropoff used when prefilling a lead from a company. */
export function getContactCompanyPrimaryDropoffLocationId(
  company: Pick<ContactCompany, "dropoffLocationIds">,
): string | undefined {
  return company.dropoffLocationIds[0];
}

/** Normalize stored terminal ids (dedupe, trim, drop empties). */
export function normalizeContactCompanyTerminalIds(ids: unknown): string[] {
  const out: string[] = [];
  if (!Array.isArray(ids)) return out;
  for (const entry of ids) {
    if (typeof entry !== "string") continue;
    const trimmed = entry.trim();
    if (!trimmed || out.includes(trimmed)) continue;
    out.push(trimmed);
  }
  return out;
}
